import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Spin, Typography } from 'antd';

import { Icon } from '@eduzz/ui-antd-theme';

import { Logo } from '@/components/globals/Logo';
import { FaceInput } from '@/components/steps/FaceDetection/FaceInput';
import { useCameraAvailable } from '@/hooks/useCameraAvailable';
import { trpcVanillaClient } from '@/lib/trpc';

type PageState = 'loading' | 'capture' | 'completed' | 'error';

const FaceCapture = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const cameraState = useCameraAvailable();

  useEffect(() => {
    if (!sessionId) {
      setPageState('error');
      return;
    }

    const openSession = async () => {
      try {
        await trpcVanillaClient.facetec.openSession.mutate({ sessionId });
        setPageState('capture');
      } catch {
        setPageState('error');
      }
    };

    openSession();
  }, [sessionId]);

  const handleSuccess = useCallback(() => {
    setPageState('completed');
  }, []);

  return (
    <div className='flex min-h-dvh flex-col bg-(--uizz-antd-color-bg-layout)'>
      <header className='flex justify-center border-b border-(--uizz-antd-color-border-secondary) px-6 py-4'>
        <Logo className='h-6' />
      </header>

      <main className='flex flex-1 flex-col items-center justify-center px-6 py-8'>
        {pageState === 'loading' && (
          <div className='flex flex-col items-center gap-4'>
            <Spin indicator={<Icon.LoaderCircle className='animate-spin text-3xl' />} />
            <Typography.Text type='secondary' className='text-sm'>
              Conectando sessão...
            </Typography.Text>
          </div>
        )}

        {pageState === 'error' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
            <Icon.TriangleAlert aria-hidden className='text-5xl text-(--uizz-antd-color-error)' />
            <Typography.Title level={4} className='mb-0'>
              Sessão inválida ou expirada
            </Typography.Title>
            <Typography.Text type='secondary' className='text-sm'>
              Gere um novo QR Code no computador e tente novamente.
            </Typography.Text>
          </div>
        )}

        {pageState === 'capture' && sessionId && cameraState === 'checking' && (
          <div className='flex flex-col items-center gap-4'>
            <Spin indicator={<Icon.LoaderCircle className='animate-spin text-3xl' />} />
            <Typography.Text type='secondary' className='text-sm'>
              Verificando câmera...
            </Typography.Text>
          </div>
        )}

        {pageState === 'capture' && sessionId && cameraState === 'unavailable' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
            <Icon.Video aria-hidden className='text-5xl text-(--uizz-antd-color-error)' />
            <Typography.Title level={4} className='mb-0'>
              Câmera não disponível
            </Typography.Title>
            <Typography.Text type='secondary' className='text-sm'>
              Não foi possível acessar a câmera deste dispositivo. Verifique as permissões do navegador e tente
              novamente.
            </Typography.Text>
          </div>
        )}

        {pageState === 'capture' && sessionId && cameraState === 'available' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-6'>
            <div className='flex flex-col items-center gap-2 text-center'>
              <Typography.Title level={4} className='mb-0'>
                Reconhecimento facial
              </Typography.Title>
              <Typography.Text type='secondary' className='text-sm'>
                Posicione seu rosto no centro da câmera, em um local bem iluminado.
              </Typography.Text>
            </div>

            <div className='w-full'>
              <FaceInput onSuccess={handleSuccess} sessionId={sessionId} />
            </div>
          </div>
        )}

        {pageState === 'completed' && (
          <div className='flex w-full max-w-sm flex-col items-center gap-4 text-center'>
            <Icon.CircleCheck aria-hidden className='text-5xl text-(--uizz-antd-color-success)' />
            <Typography.Title level={4} className='mb-0'>
              Tudo certo!
            </Typography.Title>
            <Typography.Text type='secondary' className='text-sm'>
              Reconhecimento facial concluído com sucesso. Você já pode fechar esta página e voltar ao computador.
            </Typography.Text>
          </div>
        )}
      </main>

      <footer className='px-6 py-4 text-center text-xs text-(--uizz-antd-color-text-quaternary)'>
        Eduzz &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default FaceCapture;
